import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { Json } from '@/integrations/supabase/types';
import type { 
  AcceleratorStartup, 
  AcceleratorModule,
  AcceleratorSubmission,
  AcceleratorSubmissionFile 
} from '@/lib/accelerator-types';

// Hook to fetch the logged-in founder's startup
export function useFounderStartup() {
  const { user } = useAuth();
  const [startup, setStartup] = useState<AcceleratorStartup | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStartup = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const selectClause = `
          *,
          scout_card:scout_cards(id, company_name, short_summary, primary_contact_name, primary_contact_email, technology_category, company_stage),
          cohort:cohorts(*),
          accelerator_scout_card:accelerator_scout_cards(*)
        `;

      // Primary: the user is the registered founder of the startup
      const { data, error } = await supabase
        .from('accelerator_startups')
        .select(selectClause)
        .eq('founder_user_id', user.id)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setStartup(data as unknown as AcceleratorStartup);
        setError(null);
        return;
      }

      // Fallback: the user was added as a teammate on a startup
      const email = user.email?.toLowerCase();
      if (email) {
        const { data: teammate } = await supabase
          .from('startup_teammates')
          .select('startup_id')
          .ilike('email', email)
          .maybeSingle();

        if (teammate?.startup_id) {
          const { data: teamStartup, error: teamError } = await supabase
            .from('accelerator_startups')
            .select(selectClause)
            .eq('id', teammate.startup_id)
            .maybeSingle();

          if (teamError) throw teamError;
          if (teamStartup) {
            setStartup(teamStartup as unknown as AcceleratorStartup);
            setError(null);
            return;
          }
        }
      }

      setStartup(null);
      setError('No startup found for this account');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch startup');
    } finally {
      setLoading(false);
    }
  }, [user]);


  useEffect(() => {
    fetchStartup();
  }, [fetchStartup]);

  return { startup, loading, error, refetch: fetchStartup };
}

// Hook to fetch all submissions for a startup
export function useFounderSubmissions(startupId: string | undefined) {
  const [submissions, setSubmissions] = useState<AcceleratorSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSubmissions = useCallback(async () => {
    if (!startupId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('accelerator_submissions')
        .select('*')
        .eq('startup_id', startupId)
        .order('week_number', { ascending: true });

      if (error) throw error;
      setSubmissions(data as AcceleratorSubmission[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch submissions');
    } finally {
      setLoading(false);
    }
  }, [startupId]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  return { submissions, loading, error, refetch: fetchSubmissions };
}

// Hook to manage a single week's submission
export function useSubmission(startupId: string | undefined, weekNumber: number) {
  const [submission, setSubmission] = useState<AcceleratorSubmission | null>(null);
  const [files, setFiles] = useState<AcceleratorSubmissionFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSubmission = useCallback(async () => {
    if (!startupId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('accelerator_submissions')
        .select('*')
        .eq('startup_id', startupId)
        .eq('week_number', weekNumber)
        .maybeSingle();

      if (error) throw error;
      setSubmission(data as AcceleratorSubmission | null);

      // Fetch files if submission exists — otherwise clear so the previous
      // week's uploads never bleed into this week.
      if (data) {
        const { data: filesData } = await supabase
          .from('accelerator_submission_files')
          .select('*')
          .eq('submission_id', data.id);
        setFiles((filesData || []) as AcceleratorSubmissionFile[]);
      } else {
        setFiles([]);
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch submission');
    } finally {
      setLoading(false);
    }
  }, [startupId, weekNumber]);

  // Drop the previous week's submission/files the moment the week changes so
  // stale answers can't render while the new week loads.
  useEffect(() => {
    setSubmission(null);
    setFiles([]);
    setError(null);
  }, [startupId, weekNumber]);

  useEffect(() => {
    fetchSubmission();
  }, [fetchSubmission]);


  // Save draft (create or update without marking complete)
  const saveDraft = async (textResponses: Record<string, unknown>) => {
    if (!startupId) return;

    try {
      setSaving(true);
      if (submission) {
        // Update existing
        const { error } = await supabase
          .from('accelerator_submissions')
          .update({ text_responses: textResponses as Json })
          .eq('id', submission.id);
        if (error) throw error;
      } else {
        // Create new
        const { data, error } = await supabase
          .from('accelerator_submissions')
          .insert({
            startup_id: startupId,
            week_number: weekNumber,
            text_responses: textResponses as Json,
            is_complete: false,
          })
          .select()
          .single();
        if (error) throw error;
        setSubmission(data as AcceleratorSubmission);
      }
      await fetchSubmission();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save draft');
      throw err;
    } finally {
      setSaving(false);
    }
  };

  // Submit week (mark complete and update startup progress).
  // Strict pipeline: throws on any failure so callers can keep the draft and avoid
  // showing "Week Completed" when the backend did not actually unlock the next week.
  const submitWeek = async (textResponses: Record<string, unknown>) => {
    if (!startupId) throw new Error('Missing startup');

    try {
      setSaving(true);

      if (submission) {
        const { error } = await supabase
          .from('accelerator_submissions')
          .update({
            text_responses: textResponses as Json,
            is_complete: true,
            submitted_at: new Date().toISOString(),
          })
          .eq('id', submission.id);
        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from('accelerator_submissions')
          .insert({
            startup_id: startupId,
            week_number: weekNumber,
            text_responses: textResponses as Json,
            is_complete: true,
            submitted_at: new Date().toISOString(),
          })
          .select()
          .single();
        if (error) throw error;
        setSubmission(data as AcceleratorSubmission);
      }

      // Advance startup progress via secure RPC — THROW on error.
      const { error: rpcError } = await supabase.rpc('advance_startup_week', {
        _startup_id: startupId,
        _week_number: weekNumber,
      });
      if (rpcError) throw new Error(rpcError.message || 'Failed to advance week');

      // Refetch this submission so caller sees is_complete=true.
      await fetchSubmission();

      // Verify the unlock actually happened by reading the startup row.
      // Week 15 is terminal — current_week is capped at 15, so we only require
      // it to have reached 15 (not strictly greater).
      const { data: startupRow, error: startupErr } = await supabase
        .from('accelerator_startups')
        .select('current_week')
        .eq('id', startupId)
        .single();
      if (startupErr) throw startupErr;
      const reached = startupRow?.current_week ?? 0;
      const isTerminal = weekNumber >= 15;
      const ok = isTerminal ? reached >= 15 : reached > weekNumber;
      if (!ok) {
        throw new Error(
          isTerminal
            ? 'Program completion did not register. Please retry.'
            : 'Next week was not unlocked. Please retry.',
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit week');
      throw err;
    } finally {
      setSaving(false);
    }
  };

  // Upload file
  const uploadFile = async (file: File, uploadId?: string) => {
    if (!startupId || !submission) return;

    try {
      const fileExt = file.name.split('.').pop();
      const filePath = `${startupId}/${weekNumber}/${crypto.randomUUID()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('accelerator-files')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { error: dbError } = await supabase
        .from('accelerator_submission_files')
        .insert({
          submission_id: submission.id,
          file_name: file.name,
          file_path: filePath,
          file_type: file.type,
          file_size: file.size,
          upload_id: uploadId ?? null,
        });

      if (dbError) throw dbError;

      await fetchSubmission();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload file');
      throw err;
    }
  };

  // Delete file
  const deleteFile = async (fileId: string, filePath: string) => {
    try {
      await supabase.storage
        .from('accelerator-files')
        .remove([filePath]);

      await supabase
        .from('accelerator_submission_files')
        .delete()
        .eq('id', fileId);

      await fetchSubmission();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete file');
      throw err;
    }
  };

  return { 
    submission, 
    files, 
    loading, 
    saving, 
    error, 
    saveDraft, 
    submitWeek, 
    uploadFile, 
    deleteFile,
    refetch: fetchSubmission 
  };
}

// Hook to fetch a specific module
export function useModule(weekNumber: number) {
  const [module, setModule] = useState<AcceleratorModule | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const retry = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    const fetchModule = async () => {
      // Clear previous week's module immediately so a failed/slow fetch can never
      // leave stale content on screen (every week looking identical).
      setModule(null);
      setError(null);
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('accelerator_modules')
          .select('*')
          .eq('week_number', weekNumber)
          .maybeSingle();

        if (error) throw error;
        if (cancelled) return;
        if (!data) {
          setModule(null);
          setError('Module content is not available for this week yet.');
          return;
        }
        setModule(data as AcceleratorModule);
      } catch (err) {
        if (cancelled) return;
        setModule(null);
        setError(err instanceof Error ? err.message : 'Failed to fetch module');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchModule();
    return () => {
      cancelled = true;
    };
  }, [weekNumber, reloadKey]);

  return { module, loading, error, retry };
}



// Check if a week is accessible
export function isWeekAccessible(
  weekNumber: number, 
  currentWeek: number, 
  submissions: AcceleratorSubmission[]
): boolean {
  // Week 0 is always accessible
  if (weekNumber === 0) return true;
  
  // Check if within current progress
  if (weekNumber <= currentWeek) return true;
  
  // Check for staff override (unlocked_by set)
  const submission = submissions.find(s => s.week_number === weekNumber);
  if (submission?.unlocked_by) return true;
  
  return false;
}

// Get week status
export function getWeekStatus(
  weekNumber: number,
  currentWeek: number,
  submissions: AcceleratorSubmission[]
): 'locked' | 'available' | 'current' | 'complete' {
  const submission = submissions.find(s => s.week_number === weekNumber);
  
  if (submission?.is_complete) return 'complete';
  if (weekNumber === currentWeek) return 'current';
  if (isWeekAccessible(weekNumber, currentWeek, submissions)) return 'available';
  return 'locked';
}
